import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { buildMeasurementSpectra } from '../extern/calibration'

const createDefaultCalibration = () => ({
  enabled: false,
  zero_error_enabled: false,
  spectral_enabled: false,
  stray_enabled: false,
  version: 0,
  coeffs: [],
});

const buildCalibrationStatusFromCalibration = (calibration = createDefaultCalibration()) => ({
  overall: Boolean(calibration.enabled),
  extended_status: false,
  zero_error_calib: Boolean(calibration.zero_error_enabled),
  spectral_calib: Boolean(calibration.spectral_enabled),
  stray_calib: Boolean(calibration.stray_enabled),
});

const normalizeCalibration = (payload) => {
  const base = createDefaultCalibration();

  if (!payload || typeof payload !== 'object') {
    return base;
  }

  return {
    enabled: typeof payload.enabled === 'boolean' ? payload.enabled : base.enabled,
    zero_error_enabled: typeof payload.zero_error_enabled === 'boolean' ? payload.zero_error_enabled : base.zero_error_enabled,
    spectral_enabled: typeof payload.spectral_enabled === 'boolean' ? payload.spectral_enabled : base.spectral_enabled,
    stray_enabled: typeof payload.stray_enabled === 'boolean' ? payload.stray_enabled : base.stray_enabled,
    version: Number.isFinite(payload.version) && payload.version >= 0 ? payload.version : base.version,
    coeffs: Array.isArray(payload.coeffs) ? [...payload.coeffs] : base.coeffs,
  };
};

const createDefaultSensorState = () => {
  const calibration = createDefaultCalibration();

  return {
    connected: 'disconnected',
    connectionBusy: false,
    connectionBusyMessage: '',
    lastConnectionError: '',
    deviceActionBusy: false,
    deviceActionMessage: '',
    lastDeviceError: '',
    deviceName:'',
    batteryLevel:null,
    batteryStatus: null,
    deviceMode:'idle',
    logInterval:'',
    logInterrupted:false,
    rtStart: '',
    rtEnd: '',
    deviceDateTime: '',
    calibration,
    calibrationStatus: buildCalibrationStatusFromCalibration(calibration),
    remainingBatteryLife:'',
    Sensor_FW_Version: null,
    scheduledMeasurement:{
      startTime:'',
      endTime:'',
      set:false,
      ongoing:false,
      unableToStartFlag:false,
    },
    mqttDetails:{
      uri: '',
      username: '',
      password: '',
      topic: '',
    },
    connectionTransport: null,
    accessPointStatus: false,
    mqttStatus: false,
    lowPowerMode: false,
    lowPowerPolicy: 'force_awake',
    effectiveLowPowerMode: false,
    effectiveLowPowerReason: '',
    keepAwakeRemainingSec: 0,
    lowPowerRuntime: null,
    powerSource: '',
    powerSourceConfidence: null,
    apShutdownPending: false,
    bridgeStatus: 'idle',
    bridgeVersion: '',
    bridgeError: '',
    bridgeLastCheckedAt: null,
    lowPowerSyncMultiple: '',
    lowPowerIdleSyncSec: '',
  };
};

const createDefaultSensorData = () => ({
  spd18Raw: null,
  spd18Calibrated: null,
  spd18: null,
  spd14: null,
  visualQualityMets:{
    illuminance: '',
    x_31: '',
    y_31: '',
    x_64: '',
    y_64: '',
    s_cone_irradiance: '',
    m_cone_irradiance: '',
    l_cone_irradiance: '',
    rh_irradiance: '',
    mel_irradiance: '',
    s_cone_edi: '',
    m_cone_edi: '',
    l_cone_edi: '',
    rh_edi: '',
    medi: '',
    s_cone_elr: '',
    m_cone_elr: '',
    l_cone_elr: '',
    rh_elr: '',
    mel_elr: '',
    CCT_Ohno2013: '',
    CCT_Robertson1968: '',
    CRI_Ra: '',
    color_fidelity_index_Rf: '',
    cie_1960_ucs_u: '',
    cie_1960_ucs_v: '',
    cie_1976_ucs_u_prime: '',
    cie_1976_ucs_v_prime: '',
    Duv: '',
    tm30_CCT_ohno2013: '',
    tm30_Duv: '',
    tm30_color_fidelity_index_Rf: '',
    tm30_gamut_index_Rg: '',
    tm30_Rcs_h1: '',
    tm30_Rf_h1: '',
    ppfd: '',
    Irradiance: '',
    PeakWavelength: '',
    DominantWavelength: ''
  },
  sessions: [],
  recordTable: [],
  dataPercentage: 0,
  eventLog: [],
  spd401: null,
  holdArray:[],
});

export const useSensorStore = defineStore('sensorStore', () => {
  
  const sensorState = ref(createDefaultSensorState());


  const sensorData = ref(createDefaultSensorData());
  const updateConnectedState = (payload) => {
    sensorState.value.connected = payload;
  }

  const updateConnectionBusy = (payload = {}) => {
    sensorState.value.connectionBusy = Boolean(payload.busy);
    sensorState.value.connectionBusyMessage = payload.message || '';
  }

  const updateLastConnectionError = (payload) => {
    sensorState.value.lastConnectionError = payload || '';
  }

  const updateDeviceActionBusy = (payload = {}) => {
    sensorState.value.deviceActionBusy = Boolean(payload.busy);
    sensorState.value.deviceActionMessage = payload.message || '';
  }

  const updateLastDeviceError = (payload) => {
    sensorState.value.lastDeviceError = payload || '';
  }

  const updateDeviceName = (payload) => {
    sensorState.value.deviceName = payload;
  }

  const updateBatteryLevel = (payload) => {
    sensorState.value.batteryLevel = payload;
  }

  const updatePowerSource = (payload) => {
    sensorState.value.powerSource = payload?.powerSource || '';
    sensorState.value.powerSourceConfidence = payload?.powerSourceConfidence ?? null;
  }

  const updateBatteryStatus = (payload) => {
    sensorState.value.batteryStatus = payload && typeof payload === 'object'
      ? JSON.parse(JSON.stringify(payload))
      : null;
  }

  const updateDeviceMode = (payload) => {
    sensorState.value.deviceMode = payload;
  }

  const updateLogInterval = (payload) => {
    sensorState.value.logInterval = payload;
  }

  const updateRtStart = (payload) => {
    sensorState.value.rtStart = payload;
  }

  const updateRtEnd = (payload) => {
    sensorState.value.rtEnd = payload;
  }

  const updateDeviceDateTime = (payload) => {
    sensorState.value.deviceDateTime = payload;
  }

  const updateCalibration = (payload) => {
    sensorState.value.calibration = normalizeCalibration(payload);
    sensorState.value.calibrationStatus = buildCalibrationStatusFromCalibration(sensorState.value.calibration);

    if (Array.isArray(sensorData.value.spd18Raw)) {
      const measurementSpectra = buildMeasurementSpectra(sensorData.value.spd18Raw, sensorState.value.calibration, 25.0);
      if (measurementSpectra) {
        updateMeasurementSpectra(measurementSpectra);
      }
    }
  }

  const updateRemainingBatteryLife = (payload) => {
    sensorState.value.remainingBatteryLife = payload;
  }

  const updateSensorFW_Version = (payload) => {
    sensorState.value.Sensor_FW_Version = payload;
  }

  const updateLogInterrupted = (payload) => {
    sensorState.value.logInterrupted = payload;
  }

  //sensor data updates
  const updateSpm18 = (payload) => {
    sensorData.value.spd18 = payload;
  }

  const updateSpm14 = (payload) => {
    sensorData.value.spd14 = payload;
  }

  const updateSpm18Raw = (payload) => {
    sensorData.value.spd18Raw = payload;
  }

  const updateSpm18Calibrated = (payload) => {
    sensorData.value.spd18Calibrated = payload;
  }

  const updateMeasurementSpectra = (payload) => {
    const raw = Array.isArray(payload?.raw) ? [...payload.raw] : null;
    const calibrated = Array.isArray(payload?.calibrated) ? [...payload.calibrated] : raw;

    sensorData.value.spd18Raw = raw;
    sensorData.value.spd18Calibrated = calibrated;
    sensorData.value.spd18 = calibrated;
    sensorData.value.spd14 = calibrated;
  }

  const updateSpm401 = (payload) => {
    sensorData.value.spd401 = payload;
  }

  const pushHoldArray = (payload) => {
    if(payload != null){
      sensorData.value.holdArray.push(Array.isArray(payload) ? [...payload] : payload);
    }
    
  }

  const emptyHoldArray = () => {
    sensorData.value.holdArray = [];
  }

  const updateVisualQualityMets = (payload) => {
    sensorData.value.visualQualityMets = payload;
  }

  const pushToRecordTable = (payload) => {
    sensorData.value.recordTable.push(payload);
  }

  const clearRecordTable = () => {
    sensorData.value.recordTable = [];
  }

  const pushToEventLog = (payload) => {
    sensorData.value.eventLog.push(payload);
  }

  const clearEventLog = () => {
    sensorData.value.eventLog = [];
  }

  const updateDataPercentage = (payload) => {
    sensorData.value.dataPercentage = payload;
  }

  const updateScheduleMeasurement = (payload) => {
    sensorState.value.scheduledMeasurement = payload;
  }
  const updateScheduleMeasurementState = (payload) => {
    sensorState.value.scheduledMeasurement.set = payload;
  }

  const updateScheduleMeasurementunableToStartFlag = (payload) => {
    sensorState.value.scheduledMeasurement.unableToStartFlag = payload;
  }

  const updateMqttDetails = (payload) => {
    sensorState.value.mqttDetails = payload;
  }

  const updateConnectionTransport = (payload) => {
    sensorState.value.connectionTransport = payload;
  }

  const updateSessionSummary = (payload) => {
    sensorData.value.sessions = payload;
  }

  const modifySessionSummary = (sessionIdToRemove) => {

    sensorData.value.sessions = sensorData.value.sessions.filter(session => session.session_id !== sessionIdToRemove);
  }

  const UpdateAccessPointStatus = (payload) => {
    sensorState.value.accessPointStatus = payload;
  }

  const UpdateMqttStatus = (payload) => {
    sensorState.value.mqttStatus = payload;
  }

  const updateLowPowerMode = (payload) => {
    sensorState.value.lowPowerMode = payload;
  }

  const updateLowPowerPolicy = (payload) => {
    sensorState.value.lowPowerPolicy = payload || 'force_awake';
  }

  const updateEffectiveLowPowerMode = (payload) => {
    sensorState.value.effectiveLowPowerMode = Boolean(payload);
  }

  const updateEffectiveLowPowerReason = (payload) => {
    sensorState.value.effectiveLowPowerReason = payload || '';
  }

  const updateKeepAwakeRemainingSec = (payload) => {
    sensorState.value.keepAwakeRemainingSec = Number.isFinite(payload) ? payload : 0;
  }

  const updateLowPowerRuntime = (payload) => {
    sensorState.value.lowPowerRuntime = payload && typeof payload === 'object'
      ? JSON.parse(JSON.stringify(payload))
      : null;
  }

  const updateApShutdownPending = (payload) => {
    sensorState.value.apShutdownPending = Boolean(payload);
  }

  const updateBridgeStatus = (payload = {}) => {
    sensorState.value.bridgeStatus = payload.status || 'idle';
    sensorState.value.bridgeVersion = payload.version || '';
    sensorState.value.bridgeError = payload.error || '';
    sensorState.value.bridgeLastCheckedAt = Date.now();
  }

  const updateLowPowerSyncMultiple = (payload) => {
    sensorState.value.lowPowerSyncMultiple = payload;
  }

  const updateLowPowerIdleSyncSec = (payload) => {
    sensorState.value.lowPowerIdleSyncSec = payload;
  }

  const clearDeviceSnapshot = () => {
    const {
      connected,
      connectionBusy,
      connectionBusyMessage,
      lastConnectionError,
      deviceActionBusy,
      deviceActionMessage,
      lastDeviceError,
      connectionTransport,
      bridgeStatus,
      bridgeVersion,
      bridgeError,
      bridgeLastCheckedAt,
    } = sensorState.value;

    sensorState.value = {
      ...createDefaultSensorState(),
      connected,
      connectionBusy,
      connectionBusyMessage,
      lastConnectionError,
      deviceActionBusy,
      deviceActionMessage,
      lastDeviceError,
      connectionTransport,
      bridgeStatus,
      bridgeVersion,
      bridgeError,
      bridgeLastCheckedAt,
    };
    sensorData.value = createDefaultSensorData();
  }

  const resetAllSensorStore = () => {
    sensorState.value = createDefaultSensorState();
    sensorData.value = createDefaultSensorData();
  }
  return {
    sensorState,
    sensorData,
    modifySessionSummary,
    updateSessionSummary,
    updateConnectedState,
    updateConnectionBusy,
    updateLastConnectionError,
    updateDeviceActionBusy,
    updateLastDeviceError,
    updateDeviceName,
    updateBatteryLevel,
    updatePowerSource,
    updateBatteryStatus,
    updateDeviceMode,
    updateLogInterval,
    updateRtStart,
    updateRtEnd,
    updateDeviceDateTime,
    updateCalibration,
    updateRemainingBatteryLife,
    updateSensorFW_Version,
    updateLogInterrupted,
    updateSpm18,
    updateSpm14,
    updateSpm18Raw,
    updateSpm18Calibrated,
    updateMeasurementSpectra,
    updateSpm401,
    pushHoldArray,
    emptyHoldArray,
    updateVisualQualityMets,
    pushToRecordTable,
    clearRecordTable,
    pushToEventLog,
    clearEventLog,
    updateDataPercentage,
    updateScheduleMeasurement,
    updateScheduleMeasurementState,
    updateScheduleMeasurementunableToStartFlag,
    resetAllSensorStore,
    updateMqttDetails,
    updateConnectionTransport,
    UpdateAccessPointStatus,
    UpdateMqttStatus,
    updateLowPowerMode,
    updateLowPowerPolicy,
    updateEffectiveLowPowerMode,
    updateEffectiveLowPowerReason,
    updateKeepAwakeRemainingSec,
    updateLowPowerRuntime,
    updateApShutdownPending,
    updateBridgeStatus,
    updateLowPowerSyncMultiple,
    updateLowPowerIdleSyncSec,
    clearDeviceSnapshot,
  }
});
